// kon nimi · the garden. All 116 words live on one island, regrouped into
// nine semantic districts (not the trainer's unit order — meaning order).
// Each word is a plant whose growth stage reflects how many journal
// sections have at least one entry.

import { WORDS } from "./tokiPonaData";
import { SECTION_IDS, wordSeed } from "./tokiPonaPrompts";

export const DISTRICTS = [
  {
    id: "jan-kulupu",
    name: "kulupu jan",
    gloss: "people & kinship",
    note: "The most crowded district in kon nimi — pronouns, kin, and lovers nest so close together you'll trip over an ona before you spot the olin.",
    words: ["mi", "sina", "ona", "jan", "olin", "mama", "meli", "mije", "tonsi", "kulupu", "esun", "mani", "unpa"],
  },
  {
    id: "kule-pona",
    name: "kule pona",
    gloss: "qualities & colors",
    note: "A tidy garden of adjectives and pigments, where pona and ike grow on opposite fences and the colors just want to be admired, not judged.",
    words: ["pona", "ike", "suli", "lili", "mute", "sin", "sama", "loje", "jelo", "laso", "walo", "pimeja", "kule"],
  },
  {
    id: "kasi-soweli",
    name: "kasi soweli",
    gloss: "wild & growing things",
    note: "Half zoo, half orchard, half kitchen — kijetesantakalu shares a hedge with the kasi it probably ate, and dinner is never far from the wildlife.",
    words: ["soweli", "waso", "kala", "akesi", "pipi", "kijetesantakalu", "kasi", "kili", "pan", "moku", "telo", "suwi"],
  },
  {
    id: "sijelo-lawa",
    name: "sijelo lawa",
    gloss: "body & its edges",
    note: "The body district, sensibly plotted from head (lawa) to toes (noka), though a few residents — pakala, moli — are here to remind you it doesn't always end well.",
    words: ["luka", "noka", "uta", "sijelo", "sinpin", "monsi", "insa", "poka", "lawa", "pakala", "moli", "jaki"],
  },
  {
    id: "pilin-sona",
    name: "pilin sona",
    gloss: "mind, voice & senses",
    note: "The busiest ward for thinkers and talkers, where toki can mean either speak or think depending which bench you're sitting on.",
    words: ["toki", "nimi", "lukin", "kute", "pilin", "ken", "wile", "sona", "musi", "nasa", "wawa", "mu", "kalama", "kon", "sitelen"],
  },
  {
    id: "pali-tawa",
    name: "pali tawa",
    gloss: "doing & becoming",
    note: "Nothing sits still here — this is the district of verbs in motion, from a first step (open) to a last one (pini), with a scuffle (utala) somewhere in between.",
    words: ["pali", "kama", "tawa", "awen", "jo", "pana", "alasa", "lape", "utala", "kepeken", "ante", "weka", "open", "pini"],
  },
  {
    id: "ma-sewi",
    name: "ma sewi",
    gloss: "sky, land & elements",
    note: "Sky above, stone below, and weather in between — the smallest crowd in kon nimi, but the one with the best view.",
    words: ["ma", "suno", "mun", "sewi", "anpa", "seli", "lete", "kiwen", "nena"],
  },
  {
    id: "tomo-ilo",
    name: "tomo ilo",
    gloss: "objects & home",
    note: "The hardware store of the vocabulary, stocked floor to ceiling with houses, tools, cloth, and one suspiciously generic ijo nobody can quite categorize.",
    words: ["ijo", "sike", "tomo", "ilo", "poki", "lipu", "len", "supa", "ko", "palisa", "linja", "leko", "selo", "lupa"],
  },
  {
    id: "nasin-lili",
    name: "nasin lili",
    gloss: "numbers & grammar",
    note: "The scaffolding district — small words holding up every sentence in the garden, plus the numbers, because someone has to count the fenceposts.",
    words: ["ni", "ala", "wan", "tu", "nanpa", "tenpo", "ale", "taso", "kin", "namako", "seme", "tan", "lon", "nasin"],
  },
];

const DISTRICT_OF = {};
for (const d of DISTRICTS) for (const w of d.words) DISTRICT_OF[w] = d.id;

export function districtOfWord(word) {
  return DISTRICT_OF[word] || null;
}

export const GROWTH_STAGES = ["soil", "seed", "sprout", "leaf", "flower"];

// Which journal sections have at least one saved entry for this word?
// Entries are keyed by prompt id ("selo-2"), whose prefix names the section.
export function sectionsTouched(wordEntries = {}) {
  const touched = new Set();
  for (const promptId of Object.keys(wordEntries)) {
    const section = promptId.split("-")[0];
    if (SECTION_IDS.includes(section)) touched.add(section);
  }
  return touched;
}

// 0 soil → 1 seed → 2 sprout → 3 leaf → 4 flower (four+ sections touched).
export function growthStage(wordEntries = {}) {
  return Math.min(GROWTH_STAGES.length - 1, sectionsTouched(wordEntries).size);
}

// Small deterministic offsets so the garden reads hand-planted, not gridded.
export function plantJitter(word) {
  const seed = wordSeed(word);
  return {
    dx: (seed % 13) - 6, // px, -6..6
    dy: (((seed >>> 3) % 9)) - 4, // px, -4..4
    rot: (((seed >>> 6) % 11)) - 5, // deg, -5..5
  };
}

// How many words in a district have grown past bare soil?
export function districtProgress(district, entries = {}) {
  let grown = 0;
  for (const w of district.words) {
    if (growthStage(entries[w] || {}) > 0) grown += 1;
  }
  return { grown, total: district.words.length };
}

// Sanity helper used by tests and the map: every district word must be real.
export function unknownDistrictWords() {
  return DISTRICTS.flatMap((d) => d.words.filter((w) => !WORDS[w]));
}

// A gentle daily suggestion: among the least-grown plants, pick one
// deterministically from the date, so the whole day agrees on it and
// tomorrow looks somewhere else.
export function todaysWord(dateKey, entries = {}) {
  const all = DISTRICTS.flatMap((d) => d.words);
  if (!all.length) return null;
  const stageOf = (w) => growthStage(entries[w] || {});
  const minStage = Math.min(...all.map(stageOf));
  let best = null;
  let bestScore = -1;
  for (const w of all) {
    if (stageOf(w) !== minStage) continue;
    const score = wordSeed(`${dateKey}·${w}`);
    if (score > bestScore) {
      bestScore = score;
      best = w;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// vines — learner-drawn links between words ({ from, to, note, at })

// One key per unordered pair, so kili→moku and moku→kili are the same vine.
export function linkKey(a, b) {
  return [a, b].sort().join("·");
}

export function linksOfWord(links = [], word) {
  return links.filter((l) => l.from === word || l.to === word);
}

// Why a proposed vine can't be tied — or null when it can.
export function linkError(links = [], from, to) {
  const target = (to || "").trim().toLowerCase();
  if (!target) return "name a word to tie it to";
  if (!WORDS[target]) return `"${target}" isn't one of the 116 words`;
  if (target === from) return "a word can't be tied to itself";
  if (links.some((l) => linkKey(l.from, l.to) === linkKey(from, target)))
    return `${from} and ${target} are already tied`;
  return null;
}
